import _ from 'underscore';

export const getBidMultiplierUtils = ({ i18n, state }) => {
  if (!i18n || !state) {
    return {};
  }
  const weekDayMap = {
    Monday: 'WeekDay_2',
    Tuesday: 'WeekDay_3',
    Wednesday: 'WeekDay_4',
    Thursday: 'WeekDay_5',
    Friday: 'WeekDay_6',
    Saturday: 'WeekDay_7',
    Sunday: 'WeekDay_1',
  };

  const getSegmentText = (minNight, maxNight, isLengthOfStay) => {
    if (maxNight) {
      const segment = maxNight === minNight ? minNight : `${minNight}-${maxNight}`;
      const segmentLabel = isLengthOfStay ? i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_NightsSegment_Label') : i18n.getString('HotelCenter_Overview_AdvancedBooking_Multipliers_DaysSegment_Label');

      return segmentLabel.replace(/\{0\}/g, segment);
    }
    // current node is the last one
    const addOnText = isLengthOfStay ? i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_InputNights_AddOn_Label') : i18n.getString('HotelCenter_Overview_AdvancedBooking_Multipliers_InputDays_AddOn_Label');

    return `${minNight} ${addOnText}`;
  };

  const formatStaySegments = (staySegments, isLengthOfStay) => {
    const res = [];
    const { length } = staySegments;
    let maxNight;
    let sortedStaySegments = staySegments;

    if (length > 1) {
      sortedStaySegments = _.sortBy(staySegments, item => Number(item.key));
    }

    for (let i = 0; i < length; i += 1) {
      if (i === length - 1) {
        maxNight = _.noop();
      } else {
        maxNight = (Number(sortedStaySegments[i + 1].key) - 1).toString();
      }
      res.push({
        key: sortedStaySegments[i].key,
        factor: sortedStaySegments[i].factor,
        value: getSegmentText(sortedStaySegments[i].key, maxNight, isLengthOfStay),
        strategy: sortedStaySegments[i].strategy,
      });
    }

    return res;
  };

  // only for lengh of stay and advanced booking mulipliers
  const formatDaysAndNightsMultipliers = (bidMultipliers, isLengthOfStay) => {
    const staySegments = [];

    _.each(bidMultipliers, (multiplier) => {
      staySegments.push({
        key: multiplier.key,
        factor: Math.abs(multiplier.percentage),
        strategy: multiplier.percentage >= 0 ? 'increase' : 'decrease',
      });
    });
    return formatStaySegments(staySegments, isLengthOfStay);
  };

  const deserializeBidMultipliers = (bidMultipliers) => {
    const res = {
      deviceType: [],
      dateType: [],
      siteType: [],
      userCountry: [],
      checkInDay: [],
      lengthOfStay: [],
      advancedBooking: [],
    };

    if (!bidMultipliers || bidMultipliers.length === 0) {
      return res;
    }
    _.each(bidMultipliers, (item) => {
      const multiplierFactor = item.Factor;
      let percentage = Number(multiplierFactor) * 100;

      percentage = parseFloat(percentage - 100).toFixed(2);

      if (item['@odata.type'] === '#Model.DeviceMultiplier') {
        _.each(item.DeviceTypes, (deviceType) => {
          res.deviceType.push({ deviceType, percentage });
        });
      } else if (item['@odata.type'] === '#Model.DateTypeMultiplier') {
        res.dateType.push({ dateType: item.DateType, percentage });
      } else if (item['@odata.type'] === '#Model.SiteMultiplier') {
        _.each(item.Sites, (siteType) => {
          res.siteType.push({ siteType, percentage });
        });
      } else if (item['@odata.type'] === '#Model.UserCountryMultiplier') {
        _.each(item.Countries, (country) => {
          res.userCountry.push({ country, percentage });
        });
      } else if (item['@odata.type'] === '#Model.CheckinDayOfWeekMultiplier') {
        _.each(item.DaysOfWeek, (dayOfWeek) => {
          res.checkInDay.push({ dayOfWeek, percentage });
        });
      } else if (item['@odata.type'] === '#Model.LengthOfStayMultiplier') {
        res.lengthOfStay.push({ key: item.MinimumNumberOfNights.toString(), percentage });
      } else if (item['@odata.type'] === '#Model.AdvanceBookingWindowMultiplier') {
        res.advancedBooking.push({ key: item.MinimumNumberOfDays.toString(), percentage });
      }
    });
    if (res.lengthOfStay.length > 0) {
      res.lengthOfStay = formatDaysAndNightsMultipliers(res.lengthOfStay, true);
    }
    if (res.advancedBooking.length > 0) {
      res.advancedBooking = formatDaysAndNightsMultipliers(res.advancedBooking, false);
    }

    return res;
  };

  const getMultipliersState = (input, resetFactor = false) => {
    const bidMultipliers = deserializeBidMultipliers(input);

    return _.mapObject(bidMultipliers, (multipliers, key) => {
      if (key === 'lengthOfStay' || key === 'advancedBooking') {
        if (resetFactor) {
          return _.map(multipliers, multiplier =>
            _.defaults({
              factor: 0,
            }, multiplier));
        }
        return multipliers;
      }
      const multipliersState = {};

      _.each(multipliers, (multiplier) => {
        const name = multiplier.deviceType || multiplier.dateType ||
          multiplier.siteType || multiplier.country || multiplier.dayOfWeek;
        const percentage = resetFactor ? 0 : multiplier.percentage;

        _.extend(multipliersState, {
          [name]: {
            strategy: percentage >= 0 ? 'increase' : 'decrease',
            factor: Math.abs(percentage),
          },
        });
      });
      return multipliersState;
    });
  };

  const bidMultiplierTextFormat = (name, percentage) => i18n.getString('HotelCenter_SubAccountBar_Device_Type_Multiplier_Format').replace(/\{0\}/g, name).replace(/\{1\}/g, percentage > 0 ? `+${percentage}` : percentage);

  const getDeviceTypeMultipliersText = (deviceTypeMultipliers) => {
    const deviceTypeMap = {
      Mobile: i18n.getString('HotelCenter_Overview_Multipliers_Bids_Mobile_Label'),
      Tablet: i18n.getString('HotelCenter_Overview_Multipliers_Bids_Tablet_Label'),
      Desktop: i18n.getString('HotelCenter_Overview_Multipliers_Bids_Desktop_Label'),
    };
    const deviceMultipliersText = _.map(deviceTypeMultipliers, (deviceTypeMultiplier) => {
      const { percentage } = deviceTypeMultiplier;
      const name = deviceTypeMap[deviceTypeMultiplier.deviceType];

      return bidMultiplierTextFormat(name, percentage);
    }).join(', ');

    return `${i18n.getString('HotelCenter_Overview_DeviceType_Multipliers_Label')}: ${deviceMultipliersText}`;
  };

  const getSiteTypeMultipliersText = (siteTypeMultipliers) => {
    const siteTypeMap = {
      LocalUniversal: i18n.getString('HotelCenter_Overview_SiteType_Multipliers_Local_Universal_Label'),
      MapResults: i18n.getString('HotelCenter_Overview_SiteType_Multipliers_Map_Results_Label'),
      PropertyPromotionAd: i18n.getString('HotelCenter_Overview_SiteType_Multipliers_Property_PromotionAd_Label'),
    };
    const siteMultipliersText = _.map(siteTypeMultipliers, (siteTypeMultiplier) => {
      const { percentage } = siteTypeMultiplier;
      const name = siteTypeMap[siteTypeMultiplier.siteType];

      return bidMultiplierTextFormat(name, percentage);
    }).join(', ');

    return `${i18n.getString('HotelCenter_Overview_SiteType_Multipliers_Label')}: ${siteMultipliersText}`;
  };

  const getDateTypeMultipliersText = (dateTypeMultipliers) => {
    const dateTypeMap = {
      Selected: i18n.getString('HotelCenter_Overview_DateType_Multipliers_Selected_Label'),
      Default: i18n.getString('HotelCenter_Overview_DateType_Multipliers_Default_Label'),
    };
    const dateTypeMultipliersText = _.map(dateTypeMultipliers, (dateTypeMultiplier) => {
      const { percentage } = dateTypeMultiplier;
      const name = dateTypeMap[dateTypeMultiplier.dateType];

      return bidMultiplierTextFormat(name, percentage);
    }).join(', ');

    return `${i18n.getString('HotelCenter_Overview_DateType_Multipliers_Label')}: ${dateTypeMultipliersText}`;
  };

  const getUserCountriesMultipliersText = (userCountryMultipliers) => {
    const userCountryMultipliersText = _.map(userCountryMultipliers, (userCountryMultiplier) => {
      const { percentage } = userCountryMultiplier;
      const countries = state.get('userCountries');
      const country = _.chain(countries)
        .findWhere({ Key: userCountryMultiplier.country })
        .result('Value', userCountryMultiplier.country)
        .value();

      return bidMultiplierTextFormat(country, percentage);
    }).join(', ');

    const res = `${i18n.getString('HotelCenter_Overview_UserCountry_Multipliers_Label')}: ${userCountryMultipliersText}`;

    return res;
  };

  const getCheckInDayMultipliersText = (checkInDayMultipliers) => {
    const checkInDayMultipliersText = _.map(checkInDayMultipliers, (checkInDayMultiplier) => {
      const { percentage } = checkInDayMultiplier;
      const name = i18n.getString(weekDayMap[checkInDayMultiplier.dayOfWeek]);

      return bidMultiplierTextFormat(name, percentage);
    }).join(', ');
    const res = `${i18n.getString('HotelCenter_Overview_CheckinDay_Multipliers_Label')}: ${checkInDayMultipliersText}`;

    return res;
  };

  const getLengthOfStayMultipliersText = (lengthOfStayMultipliers) => {
    const lengthOfStayMultipliersText = _.map(lengthOfStayMultipliers, (lengthOfStayMultiplier) => {
      const percentage = lengthOfStayMultiplier.factor;
      const name = lengthOfStayMultiplier.value;

      return bidMultiplierTextFormat(name, percentage);
    }).join(', ');

    const res = `${i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_Label')}: ${lengthOfStayMultipliersText}`;

    return res;
  };


  const getMultipliersText = (input, hasPreLabel = true, defaultText = '') => {
    const res = [];

    if (_.isEmpty(input)) {
      return defaultText;
    }
    const bidMultipliers = deserializeBidMultipliers(input);

    // generate device type multipliers text
    if (bidMultipliers.deviceType.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_DeviceType_Multipliers_Label_Short'));
    }

    // generate user country multipliers text
    if (bidMultipliers.userCountry.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_UserCountry_Multipliers_Label_Short'));
    }

    // generate check in day multipliers text
    if (bidMultipliers.checkInDay.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_CheckinDay_Multipliers_Label_Short'));
    }

    // generate length of stay multipliers text
    if (bidMultipliers.lengthOfStay.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_LengthOfStay_Multipliers_Label_Short'));
    }

    // generate advanced booking multipliers text
    if (bidMultipliers.advancedBooking.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_AdvancedBooking_Multipliers_Label_Short'));
    }

    // generate date type multipliers text
    if (bidMultipliers.dateType.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_DateType_Multipliers_Label_Short'));
    }

    // generate site type multipliers text
    if (bidMultipliers.siteType.length !== 0) {
      res.push(i18n.getString('HotelCenter_Overview_SiteType_Multipliers_Label_Short'));
    }

    if (res.length === 0) {
      return '';
    }
    return hasPreLabel ? `${i18n.getString('HotelCenter_Overview_Multipliers_PreLabel')}${res.join(', ')}` : res.join(', ');
  };

  const getMultipliersDetailText = (input, defaultText = '') => {
    if (_.isEmpty(input)) {
      return defaultText;
    }
    const bidMultipliers = deserializeBidMultipliers(input);

    const convert2text = {
      deviceType: getDeviceTypeMultipliersText,
      dateType: getDateTypeMultipliersText,
      siteType: getSiteTypeMultipliersText,
      userCountry: getUserCountriesMultipliersText,
      checkInDay: getCheckInDayMultipliersText,
      lengthOfStay: getLengthOfStayMultipliersText,
    };

    return _.chain(bidMultipliers)
      .map((value, key) => {
        if (_.isFunction(convert2text[key]) && !_.isEmpty(value)) {
          return convert2text[key](value);
        }
        return null;
      })
      .compact()
      .join(';')
      .value();
  };

  const addDeviceTypeBidMultipliers = bidMultiplier => (_.isUndefined(bidMultiplier.factor) ? {} : {
    DeviceTypes: [bidMultiplier.value],
    Factor: bidMultiplier.factor,
    '@odata.type': '#Model.DeviceMultiplier',
  });

  const addDateTypeBidMultipliers = bidMultiplier => (_.isUndefined(bidMultiplier.factor) ? {} : {
    DateType: bidMultiplier.value,
    Factor: bidMultiplier.factor,
    '@odata.type': '#Model.DateTypeMultiplier',
  });


  const addSiteTypeBidMultipliers = bidMultiplier => (_.isUndefined(bidMultiplier.factor) ? {} : {
    Sites: [bidMultiplier.value],
    Factor: bidMultiplier.factor,
    '@odata.type': '#Model.SiteMultiplier',
  });

  const addUserCountryBidMultipliers = bidMultiplier =>
    (_.isUndefined(bidMultiplier.factor) ? {} : {
      Countries: [bidMultiplier.value],
      Factor: bidMultiplier.factor,
      '@odata.type': '#Model.UserCountryMultiplier',
    });

  const addCheckInDayBidMultipliers = bidMultiplier =>
    (_.isUndefined(bidMultiplier.factor) ? {} : {
      DaysOfWeek: [bidMultiplier.value],
      Factor: bidMultiplier.factor,
      '@odata.type': '#Model.CheckinDayOfWeekMultiplier',
    });

  const addLengthOfStayBidMultipliers = bidMultiplier =>
    (_.isUndefined(bidMultiplier.factor) ? {} : {
      MinimumNumberOfNights: Number(bidMultiplier.value),
      Factor: bidMultiplier.factor,
      '@odata.type': '#Model.LengthOfStayMultiplier',
    });

  const addAdvancedBookingBidMultipliers = bidMultiplier =>
    (_.isUndefined(bidMultiplier.factor) ? {} : {
      MinimumNumberOfDays: Number(bidMultiplier.value),
      Factor: bidMultiplier.factor,
      '@odata.type': '#Model.AdvanceBookingWindowMultiplier',
    });

  const serializeBidMultipliers = (bidMultipliers) => {
    const res = [];

    _.each(bidMultipliers, (bidMultiplier) => {
      if (bidMultiplier.type === 'deviceType') {
        res.push(addDeviceTypeBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'dateType') {
        res.push(addDateTypeBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'siteType') {
        res.push(addSiteTypeBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'userCountry') {
        res.push(addUserCountryBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'checkInDay') {
        res.push(addCheckInDayBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'lengthOfStay') {
        res.push(addLengthOfStayBidMultipliers(bidMultiplier));
      } else if (bidMultiplier.type === 'advancedBooking') {
        res.push(addAdvancedBookingBidMultipliers(bidMultiplier));
      }
    });

    return _.filter(res, item => !_.isEmpty(item));
  };

  return {
    bidMultiplierUtils: {
      weekDayMap,
      formatStaySegments,
      getMultipliersState,
      serializeBidMultipliers,
      getMultipliersDetailText,
      getMultipliersText,
    },
  };
};

